import { cn } from "@app/lib/utils";
import { URLS } from "@app/shared";
import waterMark from "@assets/images/about-us/sector-card-mark.png";
import { Link } from "@design-system/components";
import { trans } from "@mongez/localization";
import type { SectorData } from "../data/sectorsData";
import { ArrowLink } from "./ArrowLink";

interface SectorCardProps {
  sector: SectorData;
  index: number;
}

/**
 * Individual sector card component
 * Displays sector information with hover effects and navigation
 */
export function SectorCard({ sector, index }: SectorCardProps) {
  return (
    <div
      className={cn(
        "group relative overflow-hidden",
        "h-[300px] w-[calc(100%_-_30vh)] min-w-[200px] md:h-[400px] lg:h-[600px] 3xl:h-[764px]",
        "sm:w-[200px] md:w-[280px] md:flex-shrink-0 lg:w-[350px] 3xl:w-[390px]",
        "rounded-2xl sm:rounded-3xl md:rounded-[46px]",
      )}
    >
      {/* Color overlay that slides up on hover */}
      <div
        className={cn(
          "absolute inset-0 -translate-y-full transition-transform duration-500 ease-in-out",
          "rounded-2xl group-hover:translate-y-0 sm:rounded-3xl md:rounded-[46px]",
          sector.color,
        )}
      />

      {/* Sector image */}
      <img
        src={sector.image}
        alt={trans(
          `aboutUsPage.leadership.subsidiaries.sectors.${sector.name}.title`,
        )}
        className="h-full w-full object-cover object-bottom"
      />

      {/* Content overlay */}
      <div
        className={cn(
          "absolute inset-0 flex flex-col bg-opacity-40",
          "px-5 py-8 lg:px-10 lg:py-16",
        )}
      >
        <h5
          className={cn(
            "font-bold text-white",
            "text-2xl lg:text-5xl",
          )}
        >
          {trans(sector.name)}
        </h5>

        <p
          className={cn(
            "mt-4 font-medium text-white xl:mt-16",
            "text-sm sm:text-base md:text-lg lg:text-3xl 3xl:text-2xl",
            "lg:line-clamp-7 line-clamp-4 sm:line-clamp-5 md:line-clamp-6",
          )}
        >
          {trans(sector.description)}
        </p>
      </div>

      {/* Arrow link button */}
      <div
        className={cn(
          "absolute z-20 transition-all duration-500 ease-in-out",
          "-bottom-6 sm:-bottom-8 md:-bottom-10",
          "-right-6 sm:-right-8 md:-right-10",
        )}
      >
        <Link to={URLS.sectors.view(sector.to)}>
          <div className="h-20 w-20 sm:h-24 sm:w-24 md:h-32 md:w-32 lg:h-40 lg:w-40">
            <ArrowLink color={sector.hex} />
          </div>
        </Link>
      </div>

      {/* Watermark image */}
      <img
        src={waterMark}
        alt="Sector watermark"
        className={cn(
          "absolute bottom-0 right-full z-10",
          "transition-all duration-500 ease-in-out group-hover:right-0",
        )}
      />
    </div>
  );
}
